/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react";

export interface ConversationDraft {
  message: string;
  isInternal: boolean;
  suggestionDismissed: boolean;
  activeDraftId: string | null;
  editingLanguage: 'fr' | 'guest' | null;
  // Feedback state - per conversation isolation
  feedbackSubmitted: boolean;
  feedbackRating: number;
  feedbackNote: string;
  // Suggestion edit persistence
  editedSuggestionText: string | null;
  translatedPreviewText: string | null;
  translationConfidence: number | null;
  hasTranslation: boolean | null;
}

const defaultDraft: ConversationDraft = {
  message: "",
  isInternal: false,
  suggestionDismissed: false,
  activeDraftId: null,
  editingLanguage: null,
  feedbackSubmitted: false,
  feedbackRating: 0,
  feedbackNote: "",
  editedSuggestionText: null,
  translatedPreviewText: null,
  translationConfidence: null,
  hasTranslation: null,
};

interface ConversationContextValue {
  // Selection
  selectedBookingId: number | null;
  setSelectedBookingId: (id: number | null) => void;
  
  // Per-conversation drafts (synced across pages)
  getDraft: (bookingId: number | null) => ConversationDraft;
  updateDraft: (bookingId: number | null, updates: Partial<ConversationDraft>) => void;
  clearDraft: (bookingId: number | null) => void;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

export const ConversationProvider = ({ children }: { children: ReactNode }) => {
  const draftsRef = useRef<Map<number, ConversationDraft>>(new Map());
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [, forceUpdate] = useState({});

  const getDraft = useCallback((bookingId: number | null): ConversationDraft => {
    if (!bookingId) return defaultDraft;
    return draftsRef.current.get(bookingId) || defaultDraft;
  }, []);

  const updateDraft = useCallback((bookingId: number | null, updates: Partial<ConversationDraft>) => {
    if (!bookingId) return;
    
    const current = draftsRef.current.get(bookingId) || defaultDraft;
    draftsRef.current.set(bookingId, { ...current, ...updates });
    forceUpdate({});
  }, []);

  const clearDraft = useCallback((bookingId: number | null) => {
    if (!bookingId) return;
    draftsRef.current.delete(bookingId);
    forceUpdate({});
  }, []);

  return (
    <ConversationContext.Provider
      value={{
        selectedBookingId,
        setSelectedBookingId,
        getDraft,
        updateDraft,
        clearDraft,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export const useConversation = (): ConversationContextValue => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error("useConversation must be used within a ConversationProvider");
  }
  return context;
};

// Optional hook that returns null instead of throwing if outside provider
export const useConversationOptional = (): ConversationContextValue | null => {
  return useContext(ConversationContext);
};
